/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { useState } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { Loader } from '@g17eco/atoms/loader';
import { UtrvUserRole } from '@constants/user';
import { getCurrentUser } from '@selectors/user';
import { getAnalytics } from '@services/analytics/AnalyticsService';
import { AnalyticsEvents } from '@services/analytics/AnalyticsEvents';
import { useAppSelector } from '@reducers/index';
import './styles.scss';
import { FeaturePermissions } from '@services/permissions/FeaturePermissions';
import { Delegation } from './Delegation';
import { MassDelegation } from '@g17eco/types/survey';
import { DelegationModalSurvey } from './types';
import { DelegatedUsersTable } from './DelegatedUsersTable';
import { useGetSurveyUsersQuery, useGetUtrvDelegatedWorkgroupsQuery, useMassDelegateUtrvUsersMutation } from '@api/survey-users';
import { getRootConfig } from '@selectors/globalData';
import { QueryError } from '@g17eco/molecules/query/QueryError';
import { Action } from '@constants/action';
import { UniversalTrackerValuePlain } from '../../types/universalTrackerValue';

const analytics = getAnalytics();

interface DelegationModalProps {
  isOpen: boolean;
  initiativeId: string;
  toggle: () => void;
  handleSubmit?: () => void;
  survey: DelegationModalSurvey;
  utrvs: Pick<UniversalTrackerValuePlain, '_id' | 'stakeholders' | 'initiativeId' | 'permissions'>[];
  isReadonly?: boolean;
}

const DelegationModal = ({
  isOpen,
  toggle,
  handleSubmit = () => {},
  initiativeId,
  survey,
  utrvs,
  isReadonly,
}: DelegationModalProps) => {
  const surveyId = survey._id;
  const [role, setRole] = useState<UtrvUserRole | undefined>();

  const user = useAppSelector(getCurrentUser);
  const rootConfig = useAppSelector(getRootConfig);
  const canAccessVerification = FeaturePermissions.canAccessVerification(rootConfig);
  const [massDelegate, { isLoading: isDelegating, error: delegatingError }] = useMassDelegateUtrvUsersMutation();

  const {
    data: users,
    isLoading: isLoadingUsers,
    error: usersError,
  } = useGetSurveyUsersQuery(
    {
      surveyId,
      utrvIds: utrvs.map((u) => u._id),
    },
    {
      skip: utrvs.length === 0 || !isOpen,
    },
  );

  const { data: workgroups = [], isFetching: isLoadingWorkgroups } =
    useGetUtrvDelegatedWorkgroupsQuery(
      { surveyId, utrvIds: utrvs.map((u) => u._id) },
      { skip: utrvs.length === 0 || !isOpen },
    );

  const getAnalyticsPayload = (stakeholderType: MassDelegation['roles']) => {
    return {
      initiativeId,
      surveyId,
      scopeLevel: 'utrv',
      scopeGroup: '',
      stakeholderType,
    };
  };

  // add users or workgroups to UTRVs
  const handleAddUser = async ({
    roles,
    userIds,
    workgroupIds,
  }: Pick<MassDelegation, 'roles' | 'userIds' | 'workgroupIds'>) => {
    if (!roles.length || !(userIds?.length || workgroupIds?.length)) {
      return;
    }

    setRole(undefined);

    await massDelegate({
      surveyId,
      roles,
      userIds,
      workgroupIds,
      utrvIds: utrvs.map((u) => u._id),
      action: Action.Add,
    });

    analytics.track(AnalyticsEvents.SurveyDelegationAdded, getAnalyticsPayload(Array.isArray(role) ? role[0] : role));
    handleSubmit?.();
  };
  // remove users or workgroups from UTRVs
  const handleRemoveUser = async ({ roles, userIds, workgroupIds }: Pick<MassDelegation, 'roles' | 'userIds' | 'workgroupIds'>) => {
    if (!roles.length || !(userIds?.length || workgroupIds?.length)) {
      return;
    }

    await massDelegate({
      surveyId,
      roles,
      userIds,
      workgroupIds,
      utrvIds: utrvs.map((u) => u._id),
      action: Action.Remove,
    });

    analytics.track(AnalyticsEvents.SurveyDelegationRemoved, getAnalyticsPayload(Array.isArray(role) ? role[0] : role));
    handleSubmit?.();
  };

  const getModalBody = () => {
    const error = delegatingError || usersError;
    if (error) {
      return (
        <div className='p-4'>
          <QueryError error={error} />
        </div>
      );
    }

    if (isLoadingUsers || isDelegating || isLoadingWorkgroups) {
      return (
        <div className='row'>
          <div className='col py-5'>
            <Loader />
          </div>
        </div>
      );
    }

    const delegatedUsersTableProps = {
      canAccessVerification,
      currentUser: user,
      users: users ?? { contributors: [], verifiers: [], admins: [] },
      workgroups,
      utrvs,
      survey,
      startDelegating: (userRole: UtrvUserRole) => {
        setRole(userRole);
      },
      handleRemoveUser,
      initiativeId,
      isReadonly: Boolean(isReadonly),
    };

    return (
      <ModalBody>
        {role ? (
          <Delegation
            stopDelegating={() => {
              setRole(undefined);
            }}
            surveyId={surveyId}
            delegateCallback={handleAddUser}
            role={role}
            delegatedUsers={delegatedUsersTableProps.users}
            toggle={toggle}
            canAccessVerification={canAccessVerification}
            utrvs={utrvs}
          />
        ) : (
          <DelegatedUsersTable {...delegatedUsersTableProps} />
        )}
      </ModalBody>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} backdrop='static' className='modal-lg delegate-form'>
      <ModalHeader toggle={toggle}>Delegation</ModalHeader>
      {getModalBody()}
    </Modal>
  );
};

export default DelegationModal;
